import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createContextHardened } from '../Helper/ContextHelper';
import {
  ApiSdkEvents,
  Authentication,
  AuthMechanism,
  Credentials,
  HttpApiSdk,
  JWTAuthMechanism,
  LoggedInUser,
  User,
  UserPermission,
} from '@ekkogmbh/apisdk';
import { Config } from 'src/config';
import { OAuth2AuthMechanism } from '../Helper/OAuth2AuthMechanism';
import { e2e } from 'src/ext/e2e';
import Permissions from '../Permissions';
import { EventEmitter } from 'eventemitter3';
import { AuthMechanisms } from '../Helper/AuthMechanisms';
import useStableCallback from '../Hooks/useStableCallback';
import useRequest from '../Hooks/useRequest';
import { enqueueSnackbar } from 'notistack';

const STORAGE_KEY_AUTH = 'EKKO_AUTH';

type EventType = ApiSdkEvents | string;
type EventCallback = EventEmitter.ListenerFn;

interface Context {
  api: HttpApiSdk;
  /** user provided by authentication object */
  user?: User;
  /** user entity provided by ekko-backend. Use refreshUser to update this object. */
  currentUser?: LoggedInUser;
  isAuthenticated: boolean;
  isAuthenticating: boolean;
  isRefreshingUser: boolean;
  config: Config;
  logIn: (credentials?: Credentials, restore?: boolean) => void;
  logOut: () => void;
  /** fetches user entity from backend. use to update roles etc. */
  refreshUser: () => void;
  /** If no path given, looks for the permission name on any node */
  hasPermission: (perm: Permissions, path?: string) => boolean;
  eventSubscribe: (event: EventType, callback: EventCallback, once?: boolean) => void;
  eventUnsubscribe: (event: EventType, callback?: EventCallback) => void;
}

const [EkkoContext, useEkko] = createContextHardened<Context>('Ekko API');
export default useEkko;

interface Props {
  children: React.ReactNode;
  config: Config;
}
export const EkkoProvider = ({ children, config }: Props): React.JSX.Element => {
  const apiRef = useRef<HttpApiSdk>();
  const authRef = useRef<AuthMechanism>();

  const isRestorable = useMemo(() => config.api.authMechanism !== AuthMechanisms.OAUTH2, [config.api.authMechanism]);
  const authMechanismConfig = useMemo(() => {
    const {
      api: { url, authMechanism },
      e2eSettings,
    } = config;

    return authMechanism === AuthMechanisms.OAUTH2
      ? new OAuth2AuthMechanism(e2e, e2eSettings)
      : new JWTAuthMechanism(url);
  }, [config]);

  const [isAuthenticating, startAuthenticating] = useRequest();
  const [isRefreshingUser, startRefreshingUser] = useRequest<LoggedInUser | undefined>();
  const [isAuthenticated, setAuthenticated] = useState<boolean>(false);
  const [userPermissions, setUserPermissions] = useState<UserPermission[]>();
  const [currentUser, setCurrentUser] = useState<LoggedInUser | undefined>();

  // state logging
  useEffect(() => {
    console.log('[EKKO] authenticated:', isAuthenticated);
  }, [isAuthenticated]);

  const refreshUser = useStableCallback(() => {
    startRefreshingUser(
      async () => await apiRef.current?.getCurrentUser(),
      (result) => setCurrentUser(result),
    );
  }, []);

  /**
   * Updates the authentication state without addressing the API
   * Omitting the auth parameter updates storage & local state from current authRef.
   */
  const updateAuthRef = useCallback(
    (auth?: Authentication) => {
      if (authRef.current) {
        if (auth !== undefined) {
          authRef.current.authentication = auth;
        }

        if (authRef.current?.authentication !== undefined) {
          const { authentication } = authRef.current;

          setAuthenticated(authentication.isAuthenticated);
          setUserPermissions(authentication.user?.permissions ?? []);
          localStorage.setItem(STORAGE_KEY_AUTH, JSON.stringify(authentication));
        } else {
          // something must've went wrong, restore config
          authRef.current = authMechanismConfig;
          localStorage.setItem(STORAGE_KEY_AUTH, JSON.stringify(authRef.current.authentication));
          setAuthenticated(false);
          setUserPermissions([]);
        }
      }
    },
    [authMechanismConfig],
  );

  /** Checks authentication against API, updates local auth state */
  const authCallback = useCallback(async () => {
    await apiRef.current?.isAuthenticated();
    updateAuthRef();
  }, [updateAuthRef]);

  /** Restores authentication from storage */
  const restoreAuth = useCallback(async () => {
    const storedAuth = localStorage.getItem(STORAGE_KEY_AUTH);
    if (storedAuth && authRef.current) {
      try {
        authRef.current.authentication = JSON.parse(storedAuth);
      } catch (e) {
        // ignore, let authCallback handle it
      }
    }
    await authCallback();
  }, [authCallback]);

  const logIn = useCallback(
    (credentials?: Credentials, restore: boolean = true) => {
      startAuthenticating(async () => {
        if (restore) {
          console.log('[EKKO] try restoring ...');
          await restoreAuth();
        } else {
          console.log('[EKKO] authenticating ...');
          await apiRef.current?.authenticate(credentials, authCallback);
        }
      });
    },
    [restoreAuth, authCallback],
  );

  const logOut = useCallback(() => {
    startAuthenticating(async () => {
      await authRef.current?.invalidateAuthentication();
      updateAuthRef();
    });
  }, [updateAuthRef]);

  const hasPermission = useCallback(
    (permission: Permissions, path?: string) => {
      for (const perm of userPermissions ?? []) {
        const isNameMatch = perm.permissionName == permission;
        const isPathMatch = path === undefined || perm.nodeDefinition.nodeValuesDefinition.values.join('/') === path;

        if (isNameMatch && isPathMatch) {
          return true;
        }
      }

      return false;
    },
    [userPermissions],
  );

  const eventSubscribe = useCallback((event: EventType, callback: EventCallback, once: boolean = false) => {
    if (once) {
      apiRef.current?.once(event, callback);
    } else {
      apiRef.current?.on(event, callback);
    }
  }, []);

  const eventUnsubscribe = useCallback((event: EventType, callback?: EventCallback) => {
    apiRef.current?.off(event, callback);
  }, []);

  /** init ApiSdk and Authentication */
  useEffect(() => {
    const {
      api: { url, autoRefresh },
    } = config;

    authRef.current = authMechanismConfig;
    apiRef.current = new HttpApiSdk(url, authRef.current, autoRefresh);

    apiRef.current.on(ApiSdkEvents.AUTH_EXPIRED, () => {
      console.log('[EKKO] authentication expired');
      enqueueSnackbar('Authentication expired', { variant: 'error' });
      logOut();
    });
    apiRef.current.on(ApiSdkEvents.AUTH_REFRESH_SUCCESS, () => {
      console.log('[EKKO] authentication refreshed');
      updateAuthRef();
    });
    apiRef.current.on(ApiSdkEvents.AUTH_REFRESH_FAILURE, () => {
      console.log('[EKKO] authentication refresh failed');
      logOut();
    });
    apiRef.current.on(ApiSdkEvents.AUTH_SUCCESS, () => {
      updateAuthRef();
    });
    apiRef.current.on(ApiSdkEvents.AUTH_FAILURE, () => {
      enqueueSnackbar('Authentication failed', { variant: 'error' });
      updateAuthRef();
    });

    console.log('[EKKO] initialized');
    logIn(undefined, isRestorable);
    return () => {
      apiRef.current?.removeAllListeners();
      console.log('[EKKO] ejected');
    };
  }, [updateAuthRef, isRestorable]);

  /** sync storage across contexts */
  const storageEventHandler = useStableCallback((event: StorageEvent) => {
    switch (event.key) {
      case STORAGE_KEY_AUTH:
        console.log('[EKKO] authentication context synced');
        if (event.newValue === null) {
          logOut();
        } else {
          try {
            const decoded = JSON.parse(event.newValue);
            updateAuthRef(decoded);
          } catch (e) {
            console.log('[EKKO] authentication context sync failed');
          }
        }
        break;
    }
  }, []);

  useEffect(() => {
    addEventListener('storage', storageEventHandler);
    return () => {
      removeEventListener('storage', storageEventHandler);
    };
  }, [storageEventHandler]);

  // fully initialize before rendering children
  if (apiRef.current === undefined) {
    console.log('[EKKO] initializing...');
    return <></>;
  }

  if (userPermissions === undefined) {
    // Perms may not be initialized after a refresh
    return <></>;
  }

  return (
    <EkkoContext.Provider
      value={{
        api: apiRef.current,
        user: authRef.current?.authentication.user,
        currentUser,
        config,
        isAuthenticated,
        isAuthenticating,
        isRefreshingUser,
        hasPermission,
        logIn,
        logOut,
        refreshUser,
        eventSubscribe,
        eventUnsubscribe,
      }}
    >
      {children}
    </EkkoContext.Provider>
  );
};
